import { useOktaAuth } from '@okta/okta-react';
import {
  Link,
  Route,
  useNavigate,
  useLocation,
  Routes,
} from 'react-router-dom';
import { Button, Col, Layout, Menu, Row, Image } from 'antd';
import {
  HomeOutlined,
  MenuOutlined,
  NotificationFilled,
} from '@ant-design/icons';
import logo from '../../logo.png';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import routes from './routes';
import { envs } from '../../config';
import Breadcrumbs from '../Breadcrumbs';
import Title from 'antd/lib/typography/Title';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import rfsTheme from '../../theme';
import { Optional } from '../Shared';

const { Header } = Layout;

const hamburgerId = 'header-dropdown-menu';

const defaultWhatsNewUrl =
  'https://one.rfs.nsw.gov.au/our-organisation/ict-projects/imt-personnel-and-role-assignment-solution-imt-pras#:~:text=of%20Section%2044s.-,Resources,-PRAS%20Release%20Notes';

const prasWhatsNewUrl =
  envs.REACT_APP_PRAS_WHATS_NEW_URL === undefined ||
  envs.REACT_APP_PRAS_WHATS_NEW_URL === ''
    ? defaultWhatsNewUrl
    : envs.REACT_APP_PRAS_WHATS_NEW_URL;

/**
 * Renders the application header with menu to navigate through the app and app settings on the top as fixed position.
 */
const AppHeader = () => {
  const { oktaAuth } = useOktaAuth();

  const { loggedInUser } = useLoggedInUser();

  const navigate = useNavigate();

  const currentPath = useLocation();

  // The regular antd tabbing order on the hamburger sub menu takes you to the first element
  // of the page (the rfs logo), but it should instead go back to the hamburger
  // When in the hamburger submenu, it can be exited with `esc`
  const focusOnHamburger = () => {
    document.getElementById(hamburgerId)?.focus();
  };

  const selectedButtonStyle = {
    color: 'white',
    fontWeight: '500',
    height: '100%',
    fontSize: '1em',
    borderBottom: 'none',
    display: 'flex',
    alignItems: 'center',
  };

  const logout = async () =>
    oktaAuth.signOut({
      postLogoutRedirectUri: `${
        envs.REACT_APP_LOGOUT_REDIRECT_PREFIX ?? ''
      }${encodeURIComponent(window.location.origin)}`,
    });

  const headerMenuItems: readonly ItemType[] =
    loggedInUser === undefined
      ? []
      : [
          {
            key: 'home',
            label: (
              <Button
                id="header-home-btn"
                type="text"
                icon={<HomeOutlined style={{ fontSize: '100%' }} />}
                style={{
                  ...selectedButtonStyle,
                  backgroundColor:
                    currentPath.pathname === routes.home
                      ? rfsTheme.darkRfsRed
                      : '#464749',
                }}
                size="middle"
                onClick={() => navigate(routes.home)}
              >
                Home
              </Button>
            ),
          },
          {
            key: 'roster',
            label: (
              <Button
                type="text"
                id="my-roster-btn"
                style={{
                  ...selectedButtonStyle,
                  backgroundColor:
                    currentPath.pathname === routes.roster
                      ? rfsTheme.darkRfsRed
                      : '#464749',
                }}
                size="middle"
                onClick={() => navigate(routes.roster)}
              >
                My Roster
              </Button>
            ),
          },
          {
            key: 'personnel',
            label: (
              <Button
                type="text"
                id="personnel-btn"
                style={{
                  ...selectedButtonStyle,
                  backgroundColor:
                    currentPath.pathname === routes.personnelSearch
                      ? rfsTheme.darkRfsRed
                      : '#464749',
                }}
                size="middle"
                onClick={() => navigate(routes.personnelSearch)}
              >
                Personnel
              </Button>
            ),
          },
          {
            key: 'whats-new',
            label: (
              <Button
                type="text"
                id="whats-new-btn"
                style={{
                  ...selectedButtonStyle,
                  backgroundColor: '#e5182b',
                }}
                size="middle"
                onClick={() => window.open(prasWhatsNewUrl, '_blank')}
                title="What's New?"
              >
                <NotificationFilled aria-hidden={true} />
                <span>What&apos;s New?</span>
              </Button>
            ),
            style: {
              marginLeft: 'auto',
            },
          },
        ];

  const subMenuItems: ItemType = {
    key: 'hamburger',
    label: (
      <Button
        id={hamburgerId}
        style={{
          height: '100%',
          backgroundColor: '#464749',
        }}
      >
        <MenuOutlined style={{ color: 'white' }} />
      </Button>
    ),
    style: {
      display: 'flex',
      justifyContent: 'center',
      paddingRight: '0px',
    },
    children: [
      loggedInUser === undefined
        ? []
        : [
            {
              key: 'user',
              label: 'My Account',
              onClick: () => navigate(routes.account),
              tabIndex: 1,
            },
            {
              key: 'feedback',
              label: 'Feedback',
              onClick: () => navigate(routes.feedback),
              tabIndex: 1,
            },
          ],
      {
        key: 'logout',
        label: 'Logout',
        onClick: () => void logout(),
        onBlur: () => focusOnHamburger(),
        tabIndex: 2,
      },
    ].flat(),
  };

  return (
    <Header
      style={{
        padding: 0,
        height: 'auto',
      }}
      className={'header-color'}
      role="banner"
    >
      <Row className={'header-background'}></Row>
      <Row
        style={{
          flex: 'auto',
          paddingTop: '1em',
        }}
      >
        <Col span={2} offset={2} style={{ flex: '0 0 5%' }}>
          <Link to="/">
            <Image
              src={logo}
              style={{
                width: '3vw',
                display: 'inherit',
                justifyContent: 'left',
                alignItems: 'left',
              }}
              preview={false}
              alt={'NSW RFS Logo'}
            />
          </Link>
        </Col>
        <Col
          span={10}
          style={{
            display: 'inherit',
            alignItems: 'center',
            textAlign: 'left',
            justifyContent: 'left',
          }}
        >
          <Title level={1} style={{ fontSize: '2vw' }}>
            NSW RURAL FIRE SERVICE
          </Title>
        </Col>
        <Col
          span={8}
          style={{
            display: 'inherit',
            alignItems: 'center',
            justifyContent: 'right',
          }}
        >
          <Title level={2} style={{ fontSize: '1vw' }}>
            Personnel & Role Assignment System
          </Title>
        </Col>
      </Row>
      <Optional show={loggedInUser?.user_type !== undefined}>
        <Row justify="end">
          <Col pull={2} style={{ color: rfsTheme.darkRfsRed }}>
            <b>Logged in as: {loggedInUser?.user_type}</b>
          </Col>
        </Row>
      </Optional>
      <Row style={{ paddingTop: '15px' }}>
        <Col span={20} offset={2}>
          <Menu
            id="header-menu"
            className={'header-menu'}
            mode="horizontal"
            style={{
              borderBottom: 'none',
              width: 'auto',
              color: 'black',
            }}
            role="menubar"
            items={[...headerMenuItems, subMenuItems]}
            tabIndex={-1}
          />
        </Col>
      </Row>
      <Row style={{ paddingTop: '5px' }}>
        <Optional show={currentPath.pathname !== '/'}>
          <Col span={20} offset={2}>
            <Breadcrumbs currentUser={loggedInUser} />
          </Col>
        </Optional>
      </Row>
    </Header>
  );
};

/**
 * Wraps AppHeader component with Route.
 * Wrapping with Route is needed to use useParams hook which fetches the teamId of current page
 * (only in teamRoster and Configure Teams).
 * With useParam, breadcrumb items can be created by querying teamId instead of
 * querying all the teams that the user is assigned under (which can be expensive in future).
 * @returns Component wrapped with Route that has path of all routes.
 */
const RoutedAppHeader = () => (
  <Routes>
    {Object.values(routes).map((route) => (
      <Route key={route} path={route} element={<AppHeader />} />
    ))}
  </Routes>
);

export default RoutedAppHeader;
